import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../api';
import { isPublicRoute } from '../utils/isPublicRoute';
import { fetchNavigationPreferencesOnce, invalidateNavigationPreferencesCache } from '../hooks/navigationPreferencesCache';

const STORAGE_KEY = 'kp-theme';
const VALID_THEMES = ['light', 'dark', 'contrast'];
const DEFAULT_THEME = 'contrast';
const SYNC_DEBOUNCE_MS = 600;
const LOGIN_PATH = '/login';

const ThemeCtx = createContext(null);

export function useTheme() {
  const ctx = useContext(ThemeCtx);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}

function readStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (VALID_THEMES.includes(v)) return v;
  } catch {}
  return DEFAULT_THEME;
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  document.body.classList.remove('dark', 'contrast');
  if (theme === 'dark') document.body.classList.add('dark');
  if (theme === 'contrast') document.body.classList.add('contrast');
}

export function ThemeProvider({ children }) {
  const { pathname } = useLocation();
  const [theme, setThemeState] = useState(() => readStoredTheme());
  // Satt forst nar servern svarat for en inloggad anvandare. Fore dess ar
  // localStorage bara en gissning (kan vara foregaende anvandares val eller
  // ett klick pa inloggningssidans sol/mane-knapp).
  const [remoteLoaded, setRemoteLoaded] = useState(false);
  // Senaste varde som servern kanner till — undviker onodiga PUT-anrop.
  const syncedThemeRef = useRef(null);
  const syncTimerRef = useRef(null);

  // Apply class on first render and every change.
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  // Inloggningssidan betyder att sessionen ar slut (eller inte startad).
  // Nollstall sa att nasta inloggning hamtar DEN anvandarens tema.
  useEffect(() => {
    if (pathname === LOGIN_PATH) {
      setRemoteLoaded(false);
      syncedThemeRef.current = null;
    }
  }, [pathname]);

  // Hamta temat fran servern efter inloggning. Serverns varde vinner alltid —
  // en ny anvandare utan sparat val far serverns standard (kontrast).
  //
  // Tidigare hamtades temat bara en gang nar sidan laddades. Inloggning sker
  // utan omladdning, sa anropet gick fore inloggningen (401) och nya anvandare
  // fick det tema som lag kvar i webblasaren. Nu forsoker vi igen vid varje
  // navigering tills servern svarat.
  //
  // Skippa pa publika routes (QR-flow, externa tokens, standalone-vyer) och
  // pa inloggningssidan — dar finns ingen session och anropet ger bara 401.
  useEffect(() => {
    if (remoteLoaded) return undefined;
    if (pathname === LOGIN_PATH || isPublicRoute(pathname)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Delad GET med favoriter/senaste/sektioner (A11-uppföljning).
        const data = await fetchNavigationPreferencesOnce();
        if (!data || cancelled) return;
        const remote = data && data.theme;
        if (VALID_THEMES.includes(remote)) {
          syncedThemeRef.current = remote;
          setThemeState(remote);
          try { localStorage.setItem(STORAGE_KEY, remote); } catch {}
        }
        setRemoteLoaded(true);
      } catch {
        // Network errors are non-fatal — local theme keeps working, retry on next navigation.
      }
    })();
    return () => { cancelled = true; };
  }, [pathname, remoteLoaded]);

  // Persist to backend (debounced) when the user changes theme AFTER the
  // server value is loaded. Before that there is no session to save to.
  useEffect(() => {
    if (!remoteLoaded) return undefined;
    if (theme === syncedThemeRef.current) return undefined;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      try {
        const prev = (await fetchNavigationPreferencesOnce()) || {};
        const res = await fetch(`${API_URL}/users/me/navigation-preferences`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            theme,
            favorites: Array.isArray(prev.favorites) ? prev.favorites : [],
            recent_items: Array.isArray(prev.recent_items) ? prev.recent_items : [],
            collapsed_sections: prev.collapsed_sections || { global: [], project: [] },
          }),
        });
        if (res.ok) {
          syncedThemeRef.current = theme;
          invalidateNavigationPreferencesCache();
        }
      } catch {
        // Silent — local theme is the source of truth, backend is best-effort.
      }
    }, SYNC_DEBOUNCE_MS);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [theme, remoteLoaded]);

  const setTheme = useCallback((next) => {
    if (!VALID_THEMES.includes(next)) return;
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((cur) => {
      const idx = VALID_THEMES.indexOf(cur);
      const next = VALID_THEMES[(idx + 1) % VALID_THEMES.length];
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      return next;
    });
  }, []);

  const value = { theme, setTheme, cycleTheme, themes: VALID_THEMES };
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}
